import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Lock, Download, Trash2, ShieldCheck, ShieldOff, KeyRound, AlertTriangle,
  Loader2, Copy, Check, ExternalLink, X,
} from "lucide-react";

// Phase 25.6b — In-app Privacy & Security controls.
// Mounted as a tab inside /app/settings.
// Lets the workspace owner: export data, delete workspace, enable/disable 2FA,
// view audit log.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PrivacyTab() {
  const [totpStatus, setTotpStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);  // {qr_data_url, secret, ...}
  const [setupCode, setSetupCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [confirmDeleteText, setConfirmDeleteText] = useState("");
  const [showDeleteFlow, setShowDeleteFlow] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [showDisable, setShowDisable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [workspaceId, setWorkspaceId] = useState("");

  const load = async () => {
    try {
      const [t, w, a] = await Promise.all([
        api.get("/auth/2fa/status"),
        api.get("/workspace").catch(() => ({ data: {} })),
        api.get("/admin/audit-log?limit=15").catch(() => ({ data: { items: [] } })),
      ]);
      setTotpStatus(t.data);
      setWorkspaceId(w.data?.id || w.data?.workspace_id || "");
      setAuditLog(a.data?.items || []);
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => { load(); }, []);

  const exportData = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("dara_token") || localStorage.getItem("token");
      const resp = await fetch(`${BACKEND_URL}/api/admin/security/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dara_workspace_export_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Data export downloaded. Check your downloads folder.");
      await load();
    } catch (e) {
      toast.error("Export failed: " + (e?.message || "unknown"));
    } finally { setBusy(false); }
  };

  const setup2FA = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setSetupData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Setup failed");
    } finally { setBusy(false); }
  };

  const verify2FA = async () => {
    if (!setupCode || setupCode.length < 6) { toast.error("Enter 6-digit code"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/verify", { code: setupCode });
      if (r.data?.recovery_codes) setRecoveryCodes(r.data.recovery_codes);
      toast.success("2FA enabled. Save your recovery codes!");
      setSetupData(null); setSetupCode("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const disable2FA = async () => {
    if (!disableCode || disableCode.length < 6) { toast.error("Enter 6-digit code or recovery code"); return; }
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      toast.success("2FA disabled");
      setShowDisable(false); setDisableCode("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disable failed");
    } finally { setBusy(false); }
  };

  const deleteWorkspace = async () => {
    const expected = (workspaceId || "").slice(-8);
    if (confirmDeleteText !== expected) {
      toast.error(`Type the last 8 chars of your workspace ID: ${expected}`);
      return;
    }
    if (!window.confirm("ABSOLUTELY SURE? This permanently wipes your workspace.")) return;
    setBusy(true);
    try {
      await api.post("/admin/security/delete-workspace", { confirm_text: confirmDeleteText });
      toast.success("Workspace deleted. Logging out…", { duration: 4000 });
      setTimeout(() => {
        localStorage.clear();
        window.location.href = "/";
      }, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="privacy-tab">
      {/* TRUST CENTER LINK */}
      <div className="border border-[#FFB000]/30 bg-[#FFB000]/[0.04] p-5 flex items-start gap-3">
        <Lock className="w-5 h-5 text-[#FFB000] flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000] mb-1">// PUBLIC TRUST CENTER</div>
          <p className="text-sm text-zinc-300 leading-relaxed">
            See the full breakdown of how DARA encrypts, scopes, and stores your data —
            including a downloadable one-pager you can share with your IT team or board.
          </p>
          <a href="/security" target="_blank" rel="noreferrer" data-testid="privacy-trust-link"
            className="inline-flex items-center gap-1 mt-3 font-mono text-[11px] uppercase tracking-widest text-[#FFB000] hover:underline">
            VIEW TRUST CENTER <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {/* 2FA */}
      <section className="border border-zinc-900 bg-zinc-950/40 p-5" data-testid="privacy-2fa">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// 2-FACTOR AUTHENTICATION</div>
            <h3 className="font-heading font-bold text-lg mt-1">Authenticator app (TOTP)</h3>
            <p className="text-xs text-zinc-500 mt-1.5 max-w-md leading-relaxed">
              Google Authenticator, 1Password, Authy. Adds a 6-digit code on every login.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {totpStatus?.enabled ? (
              <>
                <span className="inline-flex items-center gap-1 px-2 py-1 border border-[#00E676]/40 text-[#00E676] bg-[#00E676]/10 font-mono text-[10px] uppercase tracking-widest">
                  <ShieldCheck className="w-3 h-3" /> ENABLED
                </span>
                <button onClick={() => setShowDisable(true)} disabled={busy} data-testid="privacy-2fa-disable"
                  className="border border-[#FF6B47]/40 text-[#FF6B47] hover:bg-[#FF6B47]/10 px-3 py-2 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-2">
                  <ShieldOff className="w-3 h-3" /> DISABLE
                </button>
              </>
            ) : (
              <button onClick={setup2FA} disabled={busy || !!setupData} data-testid="privacy-2fa-enable"
                className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-4 py-2 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
                <ShieldCheck className="w-3 h-3" /> ENABLE 2FA
              </button>
            )}
          </div>
        </div>

        {/* Setup flow */}
        {setupData && (
          <div className="border-t border-zinc-900 pt-4 mt-4">
            <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
              1. Scan this QR with your authenticator app. 2. Enter the 6-digit code to confirm.
            </p>
            <div className="flex items-start gap-4 flex-wrap">
              <img src={setupData.qr_data_url} alt="2FA QR code" className="w-40 h-40 border border-zinc-800 bg-white p-2" data-testid="privacy-2fa-qr" />
              <div className="flex-1 min-w-[200px] space-y-3">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">SECRET (IF QR DOESN'T SCAN)</div>
                  <code className="block bg-black border border-zinc-800 px-3 py-2 font-mono text-[11px] text-[#FFB000] break-all select-all">{setupData.secret}</code>
                </div>
                <input type="text" value={setupCode} onChange={e => setSetupCode(e.target.value.replace(/\s/g, ""))}
                  placeholder="6-digit code" maxLength={6} data-testid="privacy-2fa-code-input"
                  className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xl text-white tracking-widest text-center focus:outline-none focus:border-[#FFB000]" />
                <div className="flex gap-2">
                  <button onClick={() => setSetupData(null)} className="border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400">CANCEL</button>
                  <button onClick={verify2FA} disabled={busy || setupCode.length < 6} data-testid="privacy-2fa-verify"
                    className="flex-1 bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90 disabled:opacity-50">
                    {busy ? "VERIFYING…" : "VERIFY & ENABLE"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recovery codes shown ONCE */}
        {recoveryCodes && (
          <div className="border-t border-zinc-900 pt-4 mt-4">
            <div className="border border-[#FFB000]/40 bg-[#FFB000]/[0.04] p-4">
              <div className="flex items-center gap-2 mb-2">
                <KeyRound className="w-4 h-4 text-[#FFB000]" />
                <div className="font-heading font-bold text-sm">Recovery codes — save now, shown ONCE</div>
              </div>
              <p className="text-xs text-zinc-400 mb-3">Each code can be used once if you lose your device.</p>
              <div className="grid grid-cols-2 gap-2 font-mono text-sm text-[#FFB000]">
                {recoveryCodes.map(rc => (
                  <code key={rc} className="bg-black border border-zinc-800 px-3 py-2 text-center select-all">{rc}</code>
                ))}
              </div>
              <button onClick={() => { navigator.clipboard.writeText(recoveryCodes.join("\n")); toast.success("Recovery codes copied"); }}
                data-testid="privacy-2fa-copy-codes"
                className="mt-3 border border-zinc-800 hover:border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 inline-flex items-center gap-2">
                <Copy className="w-3 h-3" /> COPY ALL
              </button>
              <button onClick={() => setRecoveryCodes(null)} className="ml-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white px-3 py-2">
                DONE — I'VE SAVED THEM
              </button>
            </div>
          </div>
        )}

        {/* Disable flow */}
        {showDisable && (
          <div className="border-t border-zinc-900 pt-4 mt-4">
            <p className="text-xs text-zinc-400 mb-3">Enter your current TOTP code or a recovery code to disable 2FA.</p>
            <div className="flex gap-2 flex-wrap">
              <input type="text" value={disableCode} onChange={e => setDisableCode(e.target.value)}
                placeholder="6-digit code or recovery code" data-testid="privacy-2fa-disable-input"
                className="flex-1 min-w-[200px] bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]" />
              <button onClick={() => setShowDisable(false)} className="border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400">CANCEL</button>
              <button onClick={disable2FA} disabled={busy} data-testid="privacy-2fa-disable-confirm"
                className="border border-[#FF6B47]/40 text-[#FF6B47] hover:bg-[#FF6B47]/10 px-3 py-2 font-mono text-[10px] uppercase tracking-widest">
                {busy ? "DISABLING…" : "DISABLE 2FA"}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Data export */}
      <section className="border border-zinc-900 bg-zinc-950/40 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// DATA PORTABILITY · GDPR ART. 20</div>
            <h3 className="font-heading font-bold text-lg mt-1">Export your workspace data</h3>
            <p className="text-xs text-zinc-500 mt-1.5 max-w-md leading-relaxed">
              Download a JSON file containing ALL data tied to your workspace. Encrypted secrets
              (OAuth tokens, password hashes) are redacted for safety.
            </p>
          </div>
          <button onClick={exportData} disabled={busy} data-testid="privacy-export-btn"
            className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-4 py-2 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2 flex-shrink-0">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            EXPORT JSON
          </button>
        </div>
      </section>

      {/* Audit log */}
      <section className="border border-zinc-900 bg-zinc-950/40 p-5" data-testid="privacy-audit-log">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">// RECENT SENSITIVE ACTIONS (LAST 15)</div>
        {auditLog.length === 0 && <div className="text-xs text-zinc-600 font-mono">No audit events yet.</div>}
        {auditLog.length > 0 && (
          <div className="space-y-1">
            {auditLog.map((a, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 font-mono text-[11px] py-1 border-b border-zinc-900 last:border-b-0">
                <div className="col-span-3 text-zinc-600">{a.at?.slice(0, 19).replace("T", " ")}</div>
                <div className="col-span-3 text-[#FFB000] uppercase tracking-widest text-[10px]">{a.action}</div>
                <div className="col-span-3 text-zinc-400 truncate">{a.user_email || "—"}</div>
                <div className="col-span-3 text-zinc-500 truncate">{a.detail || ""}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* DANGER ZONE — delete workspace */}
      <section className="border border-[#FF6B47]/30 bg-[#FF6B47]/[0.03] p-5" data-testid="privacy-danger-zone">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#FF6B47] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF6B47] mb-1">// DANGER ZONE · GDPR ART. 17</div>
            <h3 className="font-heading font-bold text-lg">Delete this workspace</h3>
            <p className="text-xs text-zinc-400 mt-2 leading-relaxed max-w-lg">
              Permanently wipes EVERYTHING tied to this workspace: POS transactions, DARA Briefings,
              hedge programmes, leads, case studies, audit log, connections, users. Cannot be undone.
              Export your data first if you want a copy.
            </p>
            {!showDeleteFlow ? (
              <button onClick={() => setShowDeleteFlow(true)} data-testid="privacy-delete-show"
                className="mt-3 border border-[#FF6B47]/40 text-[#FF6B47] hover:bg-[#FF6B47]/10 px-4 py-2 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-2">
                <Trash2 className="w-3 h-3" /> START DELETION FLOW
              </button>
            ) : (
              <div className="mt-4 space-y-3">
                <p className="text-xs text-zinc-300 leading-relaxed">
                  Type the last 8 characters of your workspace ID to confirm:
                  <code className="bg-black border border-zinc-800 px-2 py-1 ml-2 text-[#FFB000]">{(workspaceId || "").slice(-8)}</code>
                </p>
                <input type="text" value={confirmDeleteText} onChange={e => setConfirmDeleteText(e.target.value)}
                  placeholder="Type the 8 chars exactly" data-testid="privacy-delete-confirm-input"
                  className="w-full max-w-xs bg-black border border-[#FF6B47]/40 px-3 py-2 font-mono text-sm text-white focus:outline-none" />
                <div className="flex gap-2">
                  <button onClick={() => { setShowDeleteFlow(false); setConfirmDeleteText(""); }}
                    className="border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400">CANCEL</button>
                  <button onClick={deleteWorkspace} disabled={busy || confirmDeleteText !== (workspaceId || "").slice(-8)} data-testid="privacy-delete-confirm"
                    className="bg-[#FF6B47] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-4 py-2 hover:opacity-90 disabled:opacity-30 inline-flex items-center gap-2">
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    PERMANENTLY DELETE WORKSPACE
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
